import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { AssetStatus, DEFAULT_ASSET_STATUS } from '@/types/status';

// Tags are strings validated by pattern, not a predefined list
export type ValidFlag = string;

export interface UseAssetTagsReturn {
  tags: ValidFlag[];
  isLoading: boolean;
  error: Error | null;
  removeTag: (tag: ValidFlag) => Promise<void>;
  addTag: (tag: ValidFlag) => Promise<void>;
}

/**
 * Hook for managing asset tags
 *
 * Fetches tags for an asset and provides mutations for adding/removing tags.
 * Only includes ValidFlags in the returned tags array.
 *
 * @param assetKey - The asset key to fetch tags for (optional)
 * @returns Object with tags, loading state, error state, and mutation functions
 *
 * @example
 * ```tsx
 * const { tags, isLoading, addTag, removeTag } = useAssetTags(asset.key);
 *
 * // Display tags
 * {tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
 *
 * // Add tag
 * await addTag('sensitive');
 *
 * // Remove tag
 * await removeTag('external');
 * ```
 */
export function useAssetTags(assetKey: string | undefined): UseAssetTagsReturn {
  const { get, put } = useAxios();
  const queryClient = useQueryClient();

  // Fetch tags query
  const {
    data: rawData,
    isPending,
    error,
  } = useQuery({
    queryKey: ['asset-tags', assetKey],
    queryFn: async () => {
      if (!assetKey) {
        throw new Error('Asset key is required');
      }

      const response = await get('/my', {
        params: {
          label: 'asset',
          key: assetKey,
          exact: true,
        },
      });

      return response.data;
    },
    enabled: !!assetKey, // Only run when assetKey exists
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
  });

  // Extract tags from API response
  const tags: string[] = useMemo(() => {
    if (!rawData?.assets?.[0]?.tags) {
      return [];
    }

    return rawData.assets[0].tags;
  }, [rawData]);

  // Add tag mutation
  const addTagMutation = useMutation({
    mutationFn: async (tag: ValidFlag) => {
      if (!assetKey) {
        throw new Error('Asset key is required');
      }

      const currentData = rawData?.assets?.[0];
      const currentTags = currentData?.tags || [];

      // Prevent modifying deleted assets
      if (currentData?.status === AssetStatus.Deleted) {
        throw new Error('Cannot modify tags on deleted asset');
      }

      // Add new tag if not already present
      const newTags = currentTags.includes(tag)
        ? currentTags
        : [...currentTags, tag];

      await put('/asset', {
        key: assetKey,
        status: currentData?.status ?? DEFAULT_ASSET_STATUS,
        tags: newTags,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-tags', assetKey] });
    },
  });

  // Remove tag mutation
  const removeTagMutation = useMutation({
    mutationFn: async (tag: ValidFlag) => {
      if (!assetKey) {
        throw new Error('Asset key is required');
      }

      const currentData = rawData?.assets?.[0];
      const currentTags = currentData?.tags || [];

      // Prevent modifying deleted assets
      if (currentData?.status === AssetStatus.Deleted) {
        throw new Error('Cannot modify tags on deleted asset');
      }

      // Remove tag
      const newTags = currentTags.filter((t: string) => t !== tag);

      await put('/asset', {
        key: assetKey,
        status: currentData?.status ?? DEFAULT_ASSET_STATUS,
        tags: newTags,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-tags', assetKey] });
    },
  });

  // Query is disabled when assetKey is undefined (enabled: !!assetKey)
  // IMPORTANT: Keep the !!assetKey check - isPending can be briefly true during initial render
  // even when query is disabled. This defensive check ensures isLoading is always false
  // when no assetKey is provided, preventing UI flicker.
  return {
    tags,
    isLoading: isPending && !!assetKey,
    error: error as Error | null,
    removeTag: async (tag: ValidFlag) => {
      await removeTagMutation.mutateAsync(tag);
    },
    addTag: async (tag: ValidFlag) => {
      await addTagMutation.mutateAsync(tag);
    },
  };
}
